import React, { useState, useEffect } from 'react'
import { Slider } from '../../components/slider'
import { UILabel } from './ui-label'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UISliderProps extends Omit<React.ComponentProps<typeof Slider>, 'onValueChange'> {
  label?: string
  unit?: string
  defaultValue?: number[]
  value?: number[]
  onValueChange?: (value: number[]) => void
  showValue?: boolean
  formatValue?: (value: number) => string
  'aria-label'?: string
}

/**
 * 슬라이더 컴포넌트
 * 실시간 값 표시와 커스텀 포맷팅을 지원합니다.
 */
export const UISlider = React.forwardRef<
  React.ComponentRef<typeof Slider>,
  UISliderProps
>(({
  className,
  label,
  unit = "",
  defaultValue = [50],
  value: controlledValue,
  onValueChange,
  showValue = true,
  formatValue,
  'aria-label': ariaUILabel,
  ...props
}, ref) => {
  const [internalValue, setInternalValue] = useState<number[]>(defaultValue)
  const labelId = React.useId()

  // Use controlled value if provided, otherwise use internal state
  const value = controlledValue ?? internalValue

  useEffect(() => {
    if (controlledValue === undefined) {
      setInternalValue(defaultValue)
    }
  }, [defaultValue, controlledValue])

  const handleValueChange = (newValue: number[]) => {
    if (controlledValue === undefined) {
      setInternalValue(newValue)
    }
    onValueChange?.(newValue)
  }

  const displayValue = formatValue
    ? formatValue(value[0] ?? 0)
    : `${value[0] ?? 0}${unit}`

  // Use either provided aria-label or fallback to label
  const sliderAriaUILabel = ariaUILabel || (label ? undefined : 'Slider')

  return (
    <UIBox className="space-y-3">
      {(label || showValue) && (
        <UIBox className="flex items-center justify-between">
          {label && (
            <UILabel
              id={labelId}
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {label}
            </UILabel>
          )}
          {showValue && (
            <UITypography as="span" variant="body1" size="sm" color="muted" className="font-mono">
              {displayValue}
            </UITypography>
          )}
        </UIBox>
      )}
      <Slider
        ref={ref}
        className={cn("w-full max-w-full min-w-0 flex-shrink-0", className)}
        value={value}
        onValueChange={handleValueChange}
        aria-label={sliderAriaUILabel}
        aria-labelledby={label ? labelId : undefined}
        {...props}
      />
    </UIBox>
  )
})

UISlider.displayName = "UISlider"